import type { BadgeFields } from '@tribeplatform/gql-client/graphql'
import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  Badge,
  MutationUpdateBadgeArgs,
  Network,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  type UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthTokenKey } from '../../utils/keys/authToken.keys.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'

export const useUpdateBadge = (options?: {
  fields?: BadgeFields
  useMutationOptions?: UseMutationOptions<
    Badge,
    ClientError,
    MutationUpdateBadgeArgs
  >
}) => {
  const { useMutationOptions, fields = 'basic' } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const networkKey = getNetworkKey()
  const authTokenKey = getAuthTokenKey()

  return useMutation<Badge, ClientError, MutationUpdateBadgeArgs>(
    input => client.badge.update(input, fields),
    {
      onMutate: variables => {
        queryClient.cancelQueries(networkKey)
        queryClient.cancelQueries(authTokenKey)

        queryClient.setQueriesData<Network>(networkKey, old => ({
          ...old,
          badges: old?.badges?.map(badge => ({
            ...badge,
            ...(badge?.id === variables?.id ? variables?.input : {}),
          })),
        }))
      },
      onSettled: () => {
        queryClient.invalidateQueries(networkKey)
        queryClient.invalidateQueries(authTokenKey)
      },
      ...useMutationOptions,
    },
  )
}
